import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const CART_KEY = 'fivora_cart';

const CartContext = createContext(null);

const readStorage = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(readStorage);

  // Persist on every change
  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cartItems));
  }, [cartItems]);

  const addItem = useCallback((product, quantity = 1) => {
    setCartItems((prev) => {
      const existing = prev.find((i) => i._id === product._id);
      if (existing) {
        return prev.map((i) =>
          i._id === product._id ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [...prev, { ...product, quantity }];
    });
  }, []);

  const removeItem = useCallback((productId) => {
    setCartItems((prev) => prev.filter((i) => i._id !== productId));
  }, []);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) return;
    setCartItems((prev) =>
      prev.map((i) => (i._id === productId ? { ...i, quantity } : i))
    );
  }, []);

  const clearCartItems = useCallback(() => {
    setCartItems([]);
  }, []);

  const isInCart = useCallback(
    (productId) => cartItems.some((i) => i._id === productId),
    [cartItems]
  );

  const getItemQuantity = useCallback(
    (productId) => cartItems.find((i) => i._id === productId)?.quantity ?? 0,
    [cartItems]
  );

  const { cartTotal, cartCount } = useMemo(() => {
    return cartItems.reduce(
      (acc, item) => {
        const price = item.salePrice ?? item.price;
        acc.cartTotal += price * item.quantity;
        acc.cartCount += item.quantity;
        return acc;
      },
      { cartTotal: 0, cartCount: 0 }
    );
  }, [cartItems]);

  const value = {
    cartItems,
    cartTotal: Math.round(cartTotal * 100) / 100,
    cartCount,
    addItem,
    removeItem,
    updateQuantity,
    clearCartItems,
    isInCart,
    getItemQuantity,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
};

export default CartContext;
